import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar as CalendarIcon,
  Loader2
} from 'lucide-react';

const DAYS_FR = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
const MONTHS_FR = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

export default function AvailabilityCalendar({ 
  products = [], 
  reservations = [], 
  onDateSelect,
  onCreateReservation,
  loading = false 
}) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedProduct, setSelectedProduct] = useState('all');
  const [selectedDates, setSelectedDates] = useState({ start: null, end: null });
  const [selectingEnd, setSelectingEnd] = useState(false);

  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  // Get days in month
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Get first day of month (0 = Sunday)
  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  // Navigate months
  const prevMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth + 1, 1));
  };

  // Check if date is reserved
  const getReservationsForDate = (day) => {
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const date = new Date(dateStr);
    
    return reservations.filter(r => {
      if (selectedProduct !== 'all' && r.product_id !== selectedProduct) return false;
      
      const start = new Date(r.start_date);
      const end = new Date(r.end_date);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      date.setHours(12, 0, 0, 0);
      
      return date >= start && date <= end && 
             ['pending', 'confirmed', 'active'].includes(r.status);
    });
  };

  // Get color for cell based on reservations
  const getCellStyle = (day) => {
    const dateReservations = getReservationsForDate(day);
    const today = new Date();
    const cellDate = new Date(currentYear, currentMonth, day);
    
    // Check if selected
    if (selectedDates.start || selectedDates.end) {
      const startDate = selectedDates.start ? new Date(selectedDates.start) : null;
      const endDate = selectedDates.end ? new Date(selectedDates.end) : null;
      
      if (startDate && cellDate.toDateString() === startDate.toDateString()) {
        return 'bg-black text-white';
      }
      if (endDate && cellDate.toDateString() === endDate.toDateString()) {
        return 'bg-black text-white';
      }
      if (startDate && endDate && cellDate > startDate && cellDate < endDate) {
        return 'bg-gray-200';
      }
    }
    
    // Past dates
    if (cellDate < today && cellDate.toDateString() !== today.toDateString()) {
      return 'text-gray-300 cursor-not-allowed';
    }
    
    // Reserved dates
    if (dateReservations.length > 0) {
      const hasConfirmed = dateReservations.some(r => r.status === 'confirmed' || r.status === 'active');
      const hasPending = dateReservations.some(r => r.status === 'pending');
      
      if (hasConfirmed) return 'bg-red-100 text-red-800 border border-red-200';
      if (hasPending) return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
    }
    
    // Available
    return 'hover:bg-gray-100 cursor-pointer';
  };

  // Handle date click
  const handleDateClick = (day) => {
    const cellDate = new Date(currentYear, currentMonth, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (cellDate < today) return;
    
    const dateStr = cellDate.toISOString().split('T')[0];
    
    if (!selectingEnd) {
      // Selecting start date
      setSelectedDates({ start: dateStr, end: null });
      setSelectingEnd(true);
    } else {
      // Selecting end date
      if (new Date(dateStr) < new Date(selectedDates.start)) {
        // If end is before start, swap
        setSelectedDates({ start: dateStr, end: selectedDates.start });
      } else {
        setSelectedDates(prev => ({ ...prev, end: dateStr }));
      }
      setSelectingEnd(false);
      
      if (onDateSelect) {
        const finalDates = new Date(dateStr) < new Date(selectedDates.start)
          ? { start: dateStr, end: selectedDates.start }
          : { start: selectedDates.start, end: dateStr };
        onDateSelect(finalDates);
      }
    }
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedDates({ start: null, end: null });
    setSelectingEnd(false);
  };

  // Generate calendar grid
  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
  const totalCells = Math.ceil((daysInMonth + firstDay) / 7) * 7;
  
  const calendarDays = [];
  for (let i = 0; i < totalCells; i++) {
    const day = i - firstDay + 1;
    if (day > 0 && day <= daysInMonth) {
      calendarDays.push(day);
    } else {
      calendarDays.push(null);
    }
  }

  // Get filtered products for the selected product
  const filteredReservations = selectedProduct === 'all' 
    ? reservations 
    : reservations.filter(r => r.product_id === selectedProduct);

  // Get product name
  const getProductName = (productId) => {
    const product = products.find(p => p.id === productId);
    return product?.name_fr || 'Inconnu';
  };

  return (
    <Card className="bg-white border-gray-200" data-testid="availability-calendar">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-black flex items-center gap-2">
            <CalendarIcon className="h-5 w-5" />
            Calendrier de disponibilité
          </CardTitle>
          <Select value={selectedProduct} onValueChange={setSelectedProduct}>
            <SelectTrigger className="w-64 bg-white border-gray-300" data-testid="product-filter">
              <SelectValue placeholder="Tous les équipements" />
            </SelectTrigger>
            <SelectContent className="bg-white max-h-60">
              <SelectItem value="all">Tous les équipements</SelectItem>
              {products.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name_fr}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        ) : (
          <>
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-4">
              <Button 
                variant="outline" 
                size="icon" 
                onClick={prevMonth}
                className="border-gray-300"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <h3 className="text-lg font-semibold text-black">
                {MONTHS_FR[currentMonth]} {currentYear}
              </h3>
              <Button 
                variant="outline" 
                size="icon" 
                onClick={nextMonth}
                className="border-gray-300"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            {/* Selection Info */}
            {(selectedDates.start || selectingEnd) && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4 p-3 bg-gray-50 rounded-lg flex items-center justify-between"
              >
                <div className="text-sm">
                  {selectingEnd ? (
                    <span className="text-gray-600">
                      Début: <strong>{selectedDates.start}</strong> - Sélectionnez la date de fin
                    </span>
                  ) : selectedDates.start && selectedDates.end ? (
                    <span className="text-gray-600">
                      <strong>{selectedDates.start}</strong> → <strong>{selectedDates.end}</strong>
                    </span>
                  ) : null}
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={clearSelection}
                    className="border-gray-300"
                  >
                    Effacer
                  </Button>
                  {selectedDates.start && selectedDates.end && onCreateReservation && (
                    <Button 
                      size="sm" 
                      onClick={() => onCreateReservation(selectedDates)}
                      className="bg-black text-white hover:bg-gray-800"
                      data-testid="create-from-calendar"
                    >
                      Créer réservation
                    </Button>
                  )}
                </div>
              </motion.div>
            )}

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {/* Day Headers */}
              {DAYS_FR.map(day => (
                <div 
                  key={day} 
                  className="text-center text-sm font-medium text-gray-500 py-2"
                >
                  {day}
                </div>
              ))}
              
              {/* Calendar Days */}
              {calendarDays.map((day, index) => {
                if (!day) {
                  return <div key={index} className="h-20 p-1" />;
                }
                
                const dateReservations = getReservationsForDate(day);
                const cellStyle = getCellStyle(day);
                const today = new Date();
                const cellDate = new Date(currentYear, currentMonth, day);
                const isToday = cellDate.toDateString() === today.toDateString();
                
                return (
                  <motion.div
                    key={index}
                    whileHover={{ scale: 1.02 }}
                    className={`h-20 p-1 rounded-lg transition-colors ${cellStyle} ${isToday ? 'ring-2 ring-black' : ''}`}
                    onClick={() => handleDateClick(day)}
                    data-testid={`calendar-day-${day}`}
                  >
                    <div className="text-sm font-medium mb-1">{day}</div>
                    <div className="space-y-0.5 overflow-hidden">
                      {dateReservations.slice(0, 2).map((r, idx) => (
                        <div 
                          key={idx}
                          className="text-xs truncate px-1 py-0.5 rounded bg-white/50"
                          title={`${r.product_name} - ${r.customer_name || 'Client'}`}
                        >
                          {selectedProduct === 'all' ? r.product_name?.substring(0, 10) : (r.customer_name || 'Réservé')}
                        </div>
                      ))}
                      {dateReservations.length > 2 && (
                        <div className="text-xs text-gray-500 px-1">
                          +{dateReservations.length - 2}
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Legend */}
            <div className="flex items-center gap-6 mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-100 border border-red-200 rounded" />
                <span className="text-sm text-gray-600">Confirmé/Actif</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-yellow-100 border border-yellow-200 rounded" />
                <span className="text-sm text-gray-600">En attente</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-white border border-gray-200 rounded" />
                <span className="text-sm text-gray-600">Disponible</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-black rounded" />
                <span className="text-sm text-gray-600">Sélectionné</span>
              </div>
            </div>

            {/* Upcoming Reservations */}
            {filteredReservations.length > 0 && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <h4 className="font-medium text-black mb-3">Prochaines réservations</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {filteredReservations
                    .filter(r => new Date(r.start_date) >= new Date())
                    .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
                    .slice(0, 5)
                    .map(r => (
                      <div 
                        key={r.id}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm"
                      >
                        <div>
                          <span className="font-medium">{r.product_name}</span>
                          <span className="text-gray-500 ml-2">
                            {new Date(r.start_date).toLocaleDateString('fr-CA')} - {new Date(r.end_date).toLocaleDateString('fr-CA')}
                          </span>
                        </div>
                        <Badge className={
                          r.status === 'confirmed' || r.status === 'active' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }>
                          {r.status === 'confirmed' ? 'Confirmé' : r.status === 'active' ? 'Actif' : 'En attente'}
                        </Badge>
                      </div>
                    ))
                  }
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
